import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import "./Assistant.css";
import { API_ENDPOINTS } from "../../constants/config";
import api from "../../services/api";
import { useNeu } from "../../context/NeuContext";
import { getQuickActions, getPageGreeting, resolveActionToMessage } from "./NeuActions";

// Notification type -> MDS notification appearance + icon
const NOTIFICATION_STYLES = {
  success: { appearance: "success", icon: "check-circle" },
  error: { appearance: "error", icon: "exclamation-octagon" },
  info: { appearance: "info", icon: "info-circle" },
};

// Custom link renderer that converts links to buttons
const LinkButton = ({ href, children, neuNavigate }) => {
  const isInternalLink = href && (href.startsWith('/') || href.startsWith('#'));
  
  const handleClick = (e) => {
    if (isInternalLink) {
      e.preventDefault();
      neuNavigate(href);
    }
  };

  if (isInternalLink) {
    return (
      <McButton
        className="neu-panel-link-button"
        fit="small"
        appearance="neutral"
        variant="outlined"
        trailingicon="arrow-right"
        click={handleClick}
        title={`Navigate to ${href}`}
      >
        {children}
      </McButton>
    );
  }

  // External links open in new tab
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="neu-panel-external-link"
    >
      {children}
    </a>
  );
};

function Assistant() {
  const {
    addNotification,
    notifications,
    currentPath,
    currentPageName,
    chatOpen,
    setChatOpen,
    navigate: neuNavigate,
    executeActions,
  } = useNeu();

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const chatMessagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevIsLoadingRef = useRef(false);
  const inputRef = useRef(null);

  // Sync with NeuContext chat state
  useEffect(() => {
    if (chatOpen && !isChatOpen) {
      setIsChatOpen(true);
    }
  }, [chatOpen, isChatOpen]);

  // Get dynamic quick actions and greeting for current page
  const quickActions = getQuickActions(currentPath);
  const pageGreeting = getPageGreeting(currentPath);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.role === "user" && chatMessagesEndRef.current) {
        chatMessagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }

      if (lastMessage.role === "assistant" && prevIsLoadingRef.current && !isLoading) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            const userMessages = chatContainerRef.current.querySelectorAll('.neu-panel-message--user');
            if (userMessages.length > 0) {
              const lastUserMessage = userMessages[userMessages.length - 1];
              lastUserMessage.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        }, 0);
      }
    }
    prevIsLoadingRef.current = isLoading;
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isChatOpen]);

  // Scroll to bottom when chat opens
  useEffect(() => {
    if (isChatOpen && chatMessagesEndRef.current) {
      setTimeout(() => {
        chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  }, [isChatOpen]);

  // Unified send — handles both typed input and quick-action pills in one path
  const handleSend = useCallback(async (text, contextData = null) => {
    if (!text?.trim() || isLoading) return;

    const userText = text.trim();
    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const requestBody = {
        message: userText,
        messages: messages.map(msg => ({ role: msg.role, content: msg.content })),
      };

      // Attach context: explicit override (pill click) > auto-detect from page
      if (contextData) {
        requestBody.context = contextData;
      } else if (currentPath === '/system-messages') {
        const ec = window.systemMessageEditorContent || {};
        const prompt = ec.system_message || '';
        const jsonSchema = ec.response_format || {};
        // Always send freeform context on system-messages page
        // so the backend can generate from scratch when editor is empty
        requestBody.context = { action: 'freeform', prompt, jsonSchema };
      }

      const response = await api.post('/api/neu-chat', requestBody);

      if (!response.data) throw new Error('Empty response');

      const data = response.data;
      let assistantContent = data.response || "";

      if (data.toolResults) {
        const tr = data.toolResults;
        let toolOutput = "";
        if (tr.analysis) toolOutput = `**Review Analysis:**\n\n${tr.analysis}`;
        else if (tr.tips?.length) toolOutput = `**Optimization Tips:**\n\n${tr.tips.map(t => "• " + t).join("\n")}`;
        else if (tr.message) toolOutput = tr.message;
        if (toolOutput) assistantContent = toolOutput + (data.response ? `\n\n${data.response}` : "");
      }

      const assistantMsg = { role: "assistant", content: assistantContent };
      if (data.rewrittenContent) {
        assistantMsg.pendingChanges = data.rewrittenContent;
      }
      newMessages.push(assistantMsg);
      setMessages([...newMessages]);

      if (data.recommendations?.length) setRecommendations(data.recommendations);

      // Execute any agentic actions Neu requested (e.g. navigation)
      if (data.actions?.length) {
        await executeActions(data.actions);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      newMessages.push({
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
      });
      setMessages([...newMessages]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, currentPath, addNotification, executeActions]);

  // Submit from input box
  const submitInput = () => {
    if (!inputValue.trim()) return;
    const text = inputValue;
    setInputValue("");
    handleSend(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitInput();
    }
  };

  // Load recommended system message
  const loadRecommendedMessage = useCallback(async (messageId, messageName) => {
    try {
      const response = await api.get(`/system-messages/${messageId}`);
      const data = response.data;

      window.dispatchEvent(new CustomEvent('selectSystemMessage', { detail: data }));
      addNotification(`Loaded "${messageName}" into the editor`, 'success');
      setRecommendations([]);
    } catch (error) {
      console.error('Error loading recommended message:', error);
      addNotification('Failed to load the instruction', 'error');
    }
  }, [addNotification]);

  // Handle quick action clicks — funnels through unified handleSend
  const handleActionClick = useCallback((actionId) => {
    if (actionId === "dismiss") return;

    const resolved = resolveActionToMessage(actionId);
    if (!resolved) return;

    if (resolved.navigateTo) {
      neuNavigate(resolved.navigateTo);
      return;
    }

    if (resolved.openUrl) {
      window.open(resolved.openUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (resolved.message) {
      if (!isChatOpen) {
        setIsChatOpen(true);
        setChatOpen(true);
      }
      handleSend(resolved.message, resolved.context || null);
    }
  }, [neuNavigate, handleSend, isChatOpen, setChatOpen]);

  // Toggle / close
  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    setChatOpen(!isChatOpen);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setChatOpen(false);
  };

  // Clear conversation
  const clearChat = () => {
    setMessages([]);
    setRecommendations([]);
  };

  const applyPendingChanges = useCallback((idx, changes) => {
    window.dispatchEvent(new CustomEvent("updateSystemMessage", { detail: changes }));
    addNotification("Changes applied to the editor!", "success");
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, pendingChanges: null } : m));
  }, [addNotification]);

  const dismissPendingChanges = useCallback((idx) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, pendingChanges: null } : m));
  }, []);

  return (
    <>
      {/* Notification Toasts */}
      {notifications.length > 0 && (
        <div className="neu-notifications">
          {notifications.map(n => (
            <div key={n.id} className={`neu-notification neu-notification--${n.type}`}>
              <McNotification
                fit="small"
                verticalalign="middle"
                appearance={NOTIFICATION_STYLES[n.type]?.appearance || "neutral-weak"}
                icon={NOTIFICATION_STYLES[n.type]?.icon}
                body={n.message}
              />
            </div>
          ))}
        </div>
      )}

      {/* Trigger Button */}
      <button
        className={`neu-panel-trigger ${isChatOpen ? 'neu-panel-trigger--active' : ''}`}
        onClick={toggleChat}
        aria-label={isChatOpen ? "Close assistant" : "Open assistant"}
        title="Neu Assistant"
      >
        <span className="neu-panel-trigger-label">Ask Neu</span>
      </button>

      {/* Side Panel */}
      <aside className={`neu-panel ${isChatOpen ? 'neu-panel--open' : ''}`} aria-live="polite">
        {/* Header */}
        <div className="neu-panel-header">
          <div className="neu-panel-header-left">
            <McIcon
              className="neu-panel-header-icon"
              icon="chatbot"
              color="var(--mds_brand_appearance_primary_default_on-background-color)"
            />
            <div className="neu-panel-header-text">
              <h3 className="mds-text--medium-bold">{pageGreeting.title}</h3>
              <span className="neu-panel-header-subtitle mds-text--x-small-normal">{pageGreeting.subtitle}</span>
            </div>
          </div>
          <div className="neu-panel-header-actions">
            <McButton
              className="neu-panel-header-btn"
              appearance="inverse"
              variant="plain"
              fit="small"
              icon="plus"
              hiddenlabel
              label="Clear conversation"
              title="New conversation"
              click={clearChat}
            />
            <McButton
              className="neu-panel-header-btn"
              appearance="inverse"
              variant="plain"
              fit="small"
              icon="times"
              hiddenlabel
              label="Close panel"
              title="Close"
              click={closeChat}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="neu-panel-messages" ref={chatContainerRef}>
          {messages.length === 0 && (
            <div className="neu-panel-welcome">
              <div className="neu-panel-welcome-icon">
                <McIcon icon="chatbot" size="24" />
              </div>
              <p className="neu-panel-welcome-title mds-text--medium-normal">
                Hi, I'm <strong>Neu</strong> — your {currentPageName} assistant.
              </p>
              {quickActions.length > 0 ? (
                <>
                  <p className="neu-panel-welcome-sub mds-text--small-normal">Try one of these to get started:</p>
                  <div className="neu-panel-suggestions">
                    {quickActions.map(qa => (
                      <McButton
                        key={qa.id}
                        className="neu-panel-suggestion-btn"
                        appearance="neutral"
                        variant="outlined"
                        width="full-width"
                        justifyitems="left"
                        label={qa.label}
                        click={() => handleActionClick(qa.actionId)}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <p className="neu-panel-welcome-sub mds-text--small-normal">Ask me anything about the platform, features, or how to get started.</p>
              )}
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`neu-panel-message ${msg.role === "user" ? "neu-panel-message--user" : "neu-panel-message--assistant"}`}
            >
              {msg.role === "assistant" && (
                <div className="neu-panel-message-avatar">N</div>
              )}
              <div className="neu-panel-message-body">
                {msg.role === "assistant" ? (
                  <>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => (
                          <LinkButton href={href} neuNavigate={neuNavigate}>
                            {children}
                          </LinkButton>
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    {msg.pendingChanges && (
                      <div className="neu-pending-changes">
                        <div className="neu-pending-changes-header mds-text--x-small-bold">Suggested edits — review before applying:</div>
                        {msg.pendingChanges.prompt && (
                          <div className="neu-pending-changes-section">
                            <span className="neu-pending-changes-label mds-text--x-small-bold">System Message</span>
                            <pre className="neu-pending-changes-preview">{msg.pendingChanges.prompt.length > 300 ? msg.pendingChanges.prompt.slice(0, 300) + '…' : msg.pendingChanges.prompt}</pre>
                          </div>
                        )}
                        {msg.pendingChanges.jsonSchema && Object.keys(msg.pendingChanges.jsonSchema).length > 0 && (
                          <div className="neu-pending-changes-section">
                            <span className="neu-pending-changes-label mds-text--x-small-bold">JSON Schema</span>
                            <pre className="neu-pending-changes-preview">{JSON.stringify(msg.pendingChanges.jsonSchema, null, 2).slice(0, 300)}{JSON.stringify(msg.pendingChanges.jsonSchema, null, 2).length > 300 ? '\n…' : ''}</pre>
                          </div>
                        )}
                        <div className="neu-pending-changes-actions">
                          <McButton
                            className="neu-pending-apply"
                            fit="small"
                            appearance="primary"
                            width="full-width"
                            label="Apply Changes"
                            click={() => applyPendingChanges(idx, msg.pendingChanges)}
                          />
                          <McButton
                            className="neu-pending-dismiss"
                            fit="small"
                            appearance="neutral"
                            variant="outlined"
                            label="Dismiss"
                            click={() => dismissPendingChanges(idx)}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}

          {/* Recommendation Cards */}
          {recommendations.length > 0 && (
            <div className="neu-panel-recommendations">
              <div className="neu-panel-recommendations-header mds-text--small-bold">
                <span>Recommended Instructions</span>
                <McButton
                  className="neu-panel-recommendations-dismiss"
                  appearance="neutral"
                  variant="plain"
                  fit="small"
                  icon="times"
                  hiddenlabel
                  label="Dismiss"
                  title="Dismiss"
                  click={() => setRecommendations([])}
                />
              </div>
              {recommendations.map((rec, idx) => (
                <div key={rec.id || idx} className="neu-panel-recommendation-card">
                  <div className="neu-panel-recommendation-info">
                    <strong className="mds-text--small-bold">{rec.name}</strong>
                    {rec.reason && <p className="mds-text--x-small-normal">{rec.reason}</p>}
                  </div>
                  <McButton
                    className="neu-panel-recommendation-load"
                    fit="small"
                    appearance="neutral"
                    variant="outlined"
                    label="Load"
                    click={() => loadRecommendedMessage(rec.id, rec.name)}
                  />
                </div>
              ))}
              <McButton
                className="neu-panel-recommendation-create"
                appearance="neutral"
                variant="plain"
                fit="small"
                width="full-width"
                justifyitems="left"
                icon="plus"
                label="Create a new one instead"
                click={() => {
                  window.dispatchEvent(new CustomEvent('createNewSystemMessage'));
                  setRecommendations([]);
                  addNotification('Opening editor to create a new instruction', 'info');
                }}
              />
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="neu-panel-message neu-panel-message--assistant">
              <div className="neu-panel-message-avatar">N</div>
              <div className="neu-panel-message-body neu-panel-thinking">
                <McLoadingIndicator fit="small" hiddenlabel label="Neu is thinking" />
              </div>
            </div>
          )}

          <div ref={chatMessagesEndRef} />
        </div>

        {/* Quick Action Pills */}
        {quickActions.length > 0 && messages.length > 0 && (
          <div className="neu-panel-quick-bar">
            {quickActions.map(qa => (
              <McButton
                key={qa.id}
                className="neu-panel-quick-pill"
                fit="small"
                appearance="neutral"
                variant="outlined"
                rounded
                label={qa.label}
                click={() => handleActionClick(qa.actionId)}
                disabled={isLoading}
              />
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="neu-panel-input-area">
          <div className="neu-panel-input-row">
            <McInput
              ref={inputRef}
              type="text"
              className="neu-panel-input"
              name="neu-message"
              label="Message Neu"
              hiddenlabel
              autocomplete="off"
              placeholder="Ask Neu..."
              value={inputValue}
              input={(e) => setInputValue(e.target.value)}
              keydown={handleKeyDown}
              disabled={isLoading}
            />
            <McButton
              className="neu-panel-send"
              icon="paper-plane"
              hiddenlabel
              label="Send message"
              click={submitInput}
              disabled={isLoading || !inputValue.trim()}
            />
          </div>
          <div className="neu-panel-disclaimer mds-text--x-small-normal">
            Responses are AI-generated. This chat is monitored for training and analysis.
          </div>
        </div>
      </aside>
    </>
  );
}

export default Assistant;
